import {
  ApplicationRef,
  Component,
  ComponentRef,
  ElementRef,
  EnvironmentInjector,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  createComponent,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { FormulaValidationResult, FormulaService } from '../../../shared/services/formula.service';
import { FormulaVariablePillComponent } from '../formula-variable-pill/formula-variable-pill.component';
import { PillColor } from '../formula-variable-pill/formula-variable-pill.model';

const WORD_BOUNDARY_RE = /[\s+\-*/^()]/;

/**
 * Zero-width space used as a cursor anchor adjacent to pills.
 * contenteditable cannot place the caret directly next to a contenteditable=false
 * element, so rebuildPills inserts this invisible character as a text node before
 * the first pill and after the last pill. It is stripped during formula extraction
 * and skipped during cursor restoration so it never appears in the formula string.
 */
const CURSOR_GUARD = '\u200B';
const CURSOR_GUARD_RE = /[\n\u200B]/g;

/**
 * Rich formula input:
 * - Single contenteditable div — native cursor, selection
 * - Variables become pills only at word boundaries (space, operator, blur),
 *   so the browser owns the cursor during normal typing and no manual
 *   cursor restoration is needed on every keystroke
 * - Real FormulaVariablePillComponent instances mounted via createComponent()
 * - Formula string reconstructed by walking childNodes (text nodes + data-varname attrs)
 */
@Component({
  selector: 'motn-formula-custom-input',
  templateUrl: './formula-custom-input.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./formula-custom-input.component.scss'],
})
export class FormulaCustomInputComponent implements OnInit, OnDestroy {
  constructor(
    private formulaService: FormulaService,
    private appRef: ApplicationRef,
    private envInjector: EnvironmentInjector,
  ) {}

  @ViewChild('formulaEl') private formulaEl!: ElementRef<HTMLDivElement>;

  private currentVariableColors: Record<string, PillColor> = {};

  @Input() set variableColors(colors: Record<string, PillColor>) {
    this.currentVariableColors = colors;
    for (const [name, refs] of this.pillRefs) {
      refs.forEach(ref => ref.setInput('color', colors[name] ?? 'red'));
    }
  }

  @Output() formulaChanged = new EventEmitter<string>();
  @Output() variablesChanged = new EventEmitter<string[]>();
  @Output() validationResult = new EventEmitter<FormulaValidationResult>();
  @Output() variableRenamed = new EventEmitter<{ oldName: string; newName: string }>();

  /** Live Angular component instances: one array per variable name, one entry per occurrence. */
  private pillRefs = new Map<string, ComponentRef<FormulaVariablePillComponent>[]>();
  private formulaSubject$ = new Subject<string>();
  private validationSub?: Subscription;

  isValid = signal(false);
  validated = signal(false);
  isEmpty = signal(true);

  // ── Input events ──────────────────────────────────────────────────────────

  onInput(event: Event): void {
    const ce = this.formulaEl.nativeElement;
    // Browser inserts <br> when adjacent contenteditable=false elements are joined — remove them
    ce.querySelectorAll('br').forEach(br => br.remove());

    const inserted = (event as InputEvent).data ?? '';
    const isWordBoundary = WORD_BOUNDARY_RE.test(inserted);

    if (isWordBoundary) {
      // Word boundary just typed: rebuild DOM to convert completed variables to pills
      const formula = this.extractFormulaText(ce);
      const cursorOffset = this.getCursorOffset(ce);
      this.rebuildPills(ce, formula);
      this.setCursorFormula(ce, cursorOffset);
      this.emitChanges(formula);
    } else {
      // Non-boundary: browser owns the DOM, just extract text and emit
      this.emitChanges(this.extractFormulaText(ce));
    }
  }

  onBlur(event: FocusEvent): void {
    // If focus moved to something inside the formula element (e.g. a pill's edit input),
    // this is an internal focus transfer — don't finalise yet.
    if (event.relatedTarget && this.formulaEl.nativeElement.contains(event.relatedTarget as Node)) {
      return;
    }
    // Finalise: convert any remaining in-progress variable to a pill
    const ce = this.formulaEl.nativeElement;
    const formula = this.extractFormulaText(ce);
    this.rebuildPills(ce, formula);
    this.emitChanges(formula);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') event.preventDefault();
    // Prevent browser undo — undoing a pill deletion restores raw HTML nodes and
    // can leave the cursor trapped inside a pill (reproducible in Firefox).
    if ((event.ctrlKey || event.metaKey) && event.key === 'z') event.preventDefault();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text/plain') ?? '').replace(/\n/g, ' ');
    if (!text) return;
    const sel = window.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);

    const ce = this.formulaEl.nativeElement;
    const formula = this.extractFormulaText(ce);
    if (WORD_BOUNDARY_RE.test(text)) {
      const cursorOffset = this.getCursorOffset(ce);
      this.rebuildPills(ce, formula);
      this.setCursorFormula(ce, cursorOffset);
    }
    this.emitChanges(formula);
  }

  // ── Pill event handlers ───────────────────────────────────────────────────

  private onPillRefRemoved(ref: ComponentRef<FormulaVariablePillComponent>): void {
    for (const [name, refs] of this.pillRefs) {
      const idx = refs.indexOf(ref);
      if (idx !== -1) {
        refs.splice(idx, 1);
        if (refs.length === 0) this.pillRefs.delete(name);
        break;
      }
    }
    ref.location.nativeElement.remove();
    this.destroyPillRef(ref);
    this.emitChanges(this.extractFormulaText(this.formulaEl.nativeElement));
  }

  private onPillRefRenamed(ref: ComponentRef<FormulaVariablePillComponent>, newName: string): void {
    let oldName: string | undefined;
    for (const [name, refs] of this.pillRefs) {
      if (refs.includes(ref)) { oldName = name; break; }
    }
    if (!oldName || oldName === newName) return;

    const refs = this.pillRefs.get(oldName)!;
    refs.forEach(r => {
      r.setInput('name', newName);
      r.location.nativeElement.setAttribute('data-varname', newName);
    });

    this.pillRefs.delete(oldName);
    const existing = this.pillRefs.get(newName) ?? [];
    this.pillRefs.set(newName, [...existing, ...refs]);

    this.variableRenamed.emit({ oldName, newName });
    this.emitChanges(this.extractFormulaText(this.formulaEl.nativeElement));
  }

  // ── DOM ↔ formula string ──────────────────────────────────────────────────

  /**
   * Text-only formula extraction: walks child nodes and concatenates text content
   * and pill variable names, inserting auto-spaces at word boundaries.
   * Does not touch the Selection API — safe to call at any time.
   */
  private extractFormulaText(ce: HTMLElement): string {
    let formula = '';
    for (const node of ce.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        const content = (node.textContent ?? '').replace(CURSOR_GUARD_RE, '');
        if (content && formula && /\w$/.test(formula) && /^\w/.test(content)) formula += ' ';
        formula += content;
      } else if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName !== 'BR') {
        const varname = (node as Element).getAttribute('data-varname');
        if (varname != null) {
          if (formula && /\w$/.test(formula)) formula += ' ';
          formula += varname;
        }
      }
    }
    return formula;
  }

  /**
   * Maps the current browser cursor position to an integer offset in formula-string space.
   * Clones the DOM from the start of ce up to the cursor into a temp element, then reuses
   * extractFormulaText on that prefix — its length is the cursor offset.
   */
  private getCursorOffset(ce: HTMLElement): number {
    const sel = window.getSelection();
    const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
    if (!range || !ce.contains(range.endContainer)) return 0;

    const prefix = document.createRange();
    prefix.setStart(ce, 0);
    prefix.setEnd(range.endContainer, range.endOffset);

    const tmp = document.createElement('div');
    tmp.appendChild(prefix.cloneContents());
    return this.extractFormulaText(tmp).length;
  }

  // ── Pill rebuild ──────────────────────────────────────────────────────────

  /**
   * Rebuilds the contenteditable DOM from the formula string, converting ALL
   * variable tokens to pill components. Called only at word boundaries and blur
   * — not on every keystroke.
   */
  private rebuildPills(ce: HTMLElement, formula: string): void {
    this.destroyAllPillRefs();
    ce.replaceChildren();

    for (const segment of this.formulaService.splitSegments(formula)) {
      if (segment.isVariable) {
        const ref = this.createPillRef(segment.value);
        const refs = this.pillRefs.get(segment.value) ?? [];
        refs.push(ref);
        this.pillRefs.set(segment.value, refs);
        ce.appendChild(ref.location.nativeElement);
      } else {
        ce.appendChild(document.createTextNode(segment.value));
      }
    }

    // Ensure a zero-width space at both ends so the cursor has a real anchor
    // before the first pill and after the last pill.
    if (ce.firstChild?.nodeType !== Node.TEXT_NODE) ce.prepend(document.createTextNode(CURSOR_GUARD));
    if (ce.lastChild?.nodeType !== Node.TEXT_NODE) ce.appendChild(document.createTextNode(CURSOR_GUARD));
  }

  private destroyPillRef(ref: ComponentRef<FormulaVariablePillComponent>): void {
    this.appRef.detachView(ref.hostView);
    ref.destroy();
  }

  private destroyAllPillRefs(): void {
    for (const refs of this.pillRefs.values()) {
      refs.forEach(ref => this.destroyPillRef(ref));
    }
    this.pillRefs.clear();
  }

  private createPillRef(name: string): ComponentRef<FormulaVariablePillComponent> {
    const ref = createComponent(FormulaVariablePillComponent, { environmentInjector: this.envInjector });
    ref.setInput('name', name);
    ref.setInput('color', this.currentVariableColors[name] ?? 'red');
    ref.location.nativeElement.setAttribute('data-varname', name);
    ref.instance.removed.subscribe(() => this.onPillRefRemoved(ref));
    ref.instance.renamed.subscribe((newName: string) => this.onPillRefRenamed(ref, newName));
    this.appRef.attachView(ref.hostView);
    return ref;
  }

  // ── Cursor restoration ────────────────────────────────────────────────────

  private setCursorFormula(ce: HTMLElement, targetOffset: number): void {
    const sel = window.getSelection();
    if (!sel) return;

    const place = (range: Range) => { sel.removeAllRanges(); sel.addRange(range); };
    let remaining = targetOffset;

    for (const child of ce.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        const raw = (child as Text).data;
        let domOffset = 0;
        let formulaCount = 0;
        while (domOffset < raw.length && formulaCount < remaining) {
          if (raw[domOffset] !== CURSOR_GUARD && raw[domOffset] !== '\n') formulaCount++;
          domOffset++;
        }
        if (formulaCount === remaining) {
          // Skip past any guards at the landing position (e.g. a leading cursor anchor)
          while (domOffset < raw.length && (raw[domOffset] === CURSOR_GUARD || raw[domOffset] === '\n')) {
            domOffset++;
          }
          const range = document.createRange();
          range.setStart(child as Text, domOffset);
          range.collapse(true);
          return place(range);
        }
        remaining -= formulaCount;
      } else if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName !== 'BR') {
        const pillLen = ((child as Element).getAttribute('data-varname') ?? '').length;
        if (remaining < pillLen) {
          // Target offset lands "inside" a pill — snap cursor before it instead
          const range = document.createRange();
          range.setStartBefore(child);
          range.collapse(true);
          return place(range);
        }
        remaining -= pillLen;
        if (remaining === 0) {
          // Target offset lands exactly after this pill
          const range = document.createRange();
          range.setStartAfter(child);
          range.collapse(true);
          return place(range);
        }
      }
    }

    // Target offset is past all content — place cursor at end
    const range = document.createRange();
    range.selectNodeContents(ce);
    range.collapse(false);
    place(range);
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** Programmatically replaces the formula (e.g. when a variable is deleted from outside). */
  setFormula(formula: string): void {
    this.rebuildPills(this.formulaEl.nativeElement, formula);
    this.emitChanges(formula);
  }

  /**
   * Inserts text at the current cursor position (or at the end if the cursor is outside
   * the formula element). cursorBack moves the cursor back by that many formula-space
   * characters after insertion — use 1 for function buttons like abs() to land inside the
   * parentheses.
   */
  insertAtCursor(text: string, cursorBack = 0): void {
    const ce = this.formulaEl.nativeElement;
    const wasFocused = document.activeElement === ce;
    ce.focus();

    const sel = window.getSelection();
    const range = sel?.rangeCount ? sel.getRangeAt(0) : null;

    if (wasFocused && range && ce.contains(range.commonAncestorContainer)) {
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      range.collapse(false);
      sel!.removeAllRanges();
      sel!.addRange(range);
    } else {
      // Formula wasn't focused — insert at end
      const endRange = document.createRange();
      endRange.selectNodeContents(ce);
      endRange.collapse(false);
      endRange.insertNode(document.createTextNode(text));
      endRange.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(endRange);
    }

    const formula = this.extractFormulaText(ce);
    if (WORD_BOUNDARY_RE.test(text)) {
      const cursorOffset = Math.max(0, this.getCursorOffset(ce) - cursorBack);
      this.rebuildPills(ce, formula);
      this.setCursorFormula(ce, cursorOffset);
    }
    this.emitChanges(formula);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private lastEmittedFormula: string | undefined;

  private emitChanges(formula: string): void {
    this.isEmpty.set(!formula);
    if (formula !== this.lastEmittedFormula) {
      this.isValid.set(false);
      this.validated.set(false);
      this.lastEmittedFormula = formula;
    }
    this.formulaChanged.emit(formula);
    this.variablesChanged.emit(this.formulaService.extractVariables(formula));
    this.formulaSubject$.next(formula);
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.validationSub = this.formulaSubject$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(formula => this.formulaService.validateExpression$(formula)),
    ).subscribe(result => {
      this.validated.set(true);
      this.isValid.set(result.valid);
      this.validationResult.emit(result);
    });
  }

  ngOnDestroy(): void {
    this.validationSub?.unsubscribe();
    this.formulaSubject$.complete();
    this.destroyAllPillRefs();
  }
}
